import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChartLine, FileDown, RefreshCw } from 'lucide-react';
import TopBarActions from '../../../components/Header/TopBarActions';
import { Keys, useClassPerfSectionRanks, useSectionsList } from '../../api/hooks';
import { useAuth } from '../../../utils/Auth/AuthProvider';
import { getUserInfo } from '../../../utils/remote';
import { Button, PillTabs } from '../../components/ui';
import { shortSection } from './lib/cpBits';
import { ordinal } from '../section/sectionMath';
import { buildClassPerformancePdf, downloadPdf } from './export/pdf';
import { SchoolApi } from '../../api/client';
import ClassPerformancePage from './ClassPerformancePage';
import { Spinner, PageHeader, PanelCard, EmptyState, ErrorPanel } from '../../components/ui';
import { FaSearch } from '../../components/ui/icons';
import CoachLaunchButton from '../student_360/coach/CoachLaunchButton';
import SectionTeam from '../section/SectionTeam';
import SectionKpis from '../section/SectionKpis';
import SectionRoster from '../section/SectionRoster';
import SectionSubjects from '../section/SectionSubjects';
import SectionTrend from '../section/SectionTrend';
import SectionCycles from '../section/SectionCycles';
import { LINK_BTN } from '../../components/ui/linkButton';

/**
 * `/school/section/:id`: the section detail page. `?view=analytics` shows the
 * full class performance page preselected to this grade and section.
 */
export default function ClassPerformanceWithSection() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { role } = useAuth();
  const canExport = role === 'school_admin' || role === 'school' || role === 'admin';
  const [params] = useSearchParams();
  const sectionId = Number(id);
  const gradesQ = useQuery({
    queryKey: ['analytics.grades'],
    queryFn: () => SchoolApi.analyticsGrades(),
  });

  const grades = (gradesQ.data?.grades ?? []) as any[];
  let grade: any;
  let section: any;
  for (const g of grades) {
    const s = Array.isArray(g.sections) ? g.sections.find((x: any) => x.id === sectionId) : undefined;
    if (s) { grade = g; section = s; break; }
  }
  // Subtitle figures: the homeroom teacher and the section's rank among its grade's sections.
  // Both queries are shared with the page body, and a failure just leaves that part of the subtitle out.
  const sectionsQ = useSectionsList(grade?.id ?? null);
  // The analytics view never shows the rank, so it does not ask for it.
  const ranksQ = useClassPerfSectionRanks({ grade: params.get('view') === 'analytics' ? null : grade?.id ?? null });

  if (gradesQ.isLoading) {
    return <PanelCard><div className="flex justify-center py-8"><Spinner size="md" /></div></PanelCard>;
  }
  if (gradesQ.isError) return <ErrorPanel />;

  if (!grade || Number.isNaN(sectionId)) {
    return (
      <PanelCard>
        <EmptyState icon={<FaSearch />} title="Section not found"
          body={<Link className="font-semibold text-ink underline decoration-accent decoration-2 underline-offset-4" to="/class-performance">Back to Class performance</Link>} />
      </PanelCard>
    );
  }

  if (params.get('view') === 'analytics') {
    return (
      <>
        <Link to={`/school/section/${sectionId}`} className="mb-3 inline-flex items-center text-sm font-semibold text-ink hover:underline">
          ← Back to {grade.name} · Section {section.name}
        </Link>
        <ClassPerformancePage initialGradeId={grade.id} initialSectionId={sectionId} />
      </>
    );
  }

  const count = section.student_count ?? 0;
  const short = shortSection(grade.name, section.name) || `Section ${section.name}`;
  const homeroomT = sectionsQ.data?.sections.find((x) => x.id === sectionId)?.homeroom_teacher ?? null;
  const homeroomName = homeroomT ? `${homeroomT.first_name ?? ''} ${homeroomT.last_name ?? ''}`.trim() || homeroomT.username : null;
  const ranked = (ranksQ.data?.sections ?? []).filter((r) => r.avg_score != null && r.rank != null);
  const mine = ranked.find((r) => r.id === sectionId);
  const subtitle = [
    `${count} ${count === 1 ? 'student' : 'students'}`,
    homeroomName ? `homeroom ${homeroomName}` : null,
    mine ? `${ordinal(mine.rank as number)} of ${ranked.length} sections in ${grade.name}` : null,
  ].filter(Boolean).join(' · ');

  async function exportPdf() {
    let schoolName = '';
    try {
      const info = await getUserInfo(localStorage.getItem('token') || undefined);
      schoolName = (info?.institution_name as string) ?? '';
    } catch { /* the PDF is still useful without the school name */ }
    const doc = buildClassPerformancePdf({
      schoolName,
      gradeName: grade.name,
      sectionName: `${grade.name} Section ${section.name}`,
      subjectName: undefined,
      generatedAt: new Date().toISOString().slice(0, 10),
      overview: qc.getQueryData<any>(Keys.classPerfOverview({ grade: grade.id, section: sectionId, subject: 'all' })),
      subjects: qc.getQueryData<any>(Keys.classPerfBySubject({ grade: grade.id, section: sectionId }))?.subjects,
      trends: qc.getQueryData<any>(Keys.classPerfTrends({ grade: grade.id, section: sectionId, subject: 'all' })),
    });
    downloadPdf(`section-${grade.name}-${section.name}.pdf`.replace(/\s+/g, '-'), doc);
  }

  return (
    <>
      <TopBarActions>
        <Link to={`/school/section/${sectionId}?view=analytics#cycles`}
          className="order-last inline-flex min-h-[44px] items-center gap-2 rounded-[12px] border border-accent bg-accent px-4 text-sm font-semibold text-navy no-underline hover:text-navy hover:brightness-105">
          <RefreshCw size={18} strokeWidth={1.8} aria-hidden />Start remediation
        </Link>
      </TopBarActions>
      <nav aria-label="Breadcrumb" className="mb-3.5">
        <ol className="flex flex-wrap items-center gap-1.5 text-[13px] text-muted">
          <li className="flex items-center gap-1.5"><Link className="font-semibold text-muted-2 no-underline hover:underline hover:underline-offset-[3px]" to="/class-performance">Class performance</Link><span aria-hidden>/</span></li>
          <li className="flex items-center gap-1.5"><Link className="font-semibold text-muted-2 no-underline hover:underline hover:underline-offset-[3px]" to="/class-performance">{grade.name}</Link><span aria-hidden>/</span></li>
          <li className="flex items-center gap-1.5"><span aria-current="page">Section {section.name}</span></li>
        </ol>
      </nav>
      <PageHeader
        eyebrow="Section"
        title={`${grade.name} · Section ${section.name}`}
        subtitle={subtitle}
        actions={<>
          <CoachLaunchButton inline label={`Ask coach about ${short}`} scope={{ type: 'section', id: sectionId, label: `${grade.name} Section ${section.name}` }} />
          {canExport && (
            <Button variant="ghost" icon={<FileDown size={18} strokeWidth={1.8} />} onClick={exportPdf}>Export PDF</Button>
          )}
          <Link to={`/school/section/${sectionId}?view=analytics`}
            className={LINK_BTN}>
            <ChartLine size={18} strokeWidth={1.8} aria-hidden />Full analytics
          </Link>
        </>}
      />
      <div className="mb-5 flex items-start gap-3.5">
        <span className="pt-[11px] font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Section</span>
        <div className="min-w-0 flex-1">
          <PillTabs scroll dense label="Switch section" value={String(sectionId)} onChange={(k) => navigate(`/school/section/${k}`)}
            options={grades.flatMap((g: any) => (Array.isArray(g.sections) ? g.sections : []).map((x: any) => ({ key: String(x.id), label: `${g.name} · ${x.name}` })))} />
        </div>
      </div>
      <div className="mb-[18px] grid items-start gap-[18px] min-[1181px]:grid-cols-12">
        <div className="min-w-0 min-[1181px]:col-span-5"><SectionTeam gradeId={grade.id} sectionId={sectionId} studentCount={count} /></div>
        <div className="min-w-0 min-[1181px]:col-span-7"><SectionKpis gradeId={grade.id} sectionId={sectionId} gradeName={grade.name} /></div>
      </div>
      <SectionRoster gradeId={grade.id} sectionId={sectionId} gradeName={grade.name} sectionName={section.name} />
      <div className="mb-5 grid items-start gap-[18px] min-[1181px]:grid-cols-12">
        <div className="min-w-0 min-[1181px]:col-span-7"><SectionSubjects gradeId={grade.id} sectionId={sectionId} gradeName={grade.name} sectionName={section.name} /></div>
        <div className="min-w-0 space-y-[18px] min-[1181px]:col-span-5">
          <SectionTrend gradeId={grade.id} sectionId={sectionId} sectionName={section.name} gradeName={grade.name} />
          <SectionCycles gradeId={grade.id} sectionId={sectionId} gradeName={grade.name} sectionName={section.name} />
        </div>
      </div>
    </>
  );
}
